import { X } from 'phosphor-react';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../../../components/Button';
import Input from '../../../components/Input';
import InputMask from '../../../components/InputMask';
import {
  apiLaureano,
  apiLaureanoSoul,
  apiPhato,
  CEDAPP_API_KEY,
} from '../../../services/api';
import * as S from './styles';

interface PhoneDialogProps {
  handleCloseModal: () => void;
}

interface PhoneData {
  nrDddCelular: string | null;
  nrCelular: string | null;
}

const apiKeyHeaders = { 'x-api-key': CEDAPP_API_KEY };
const phoneApis = [
  { name: 'Phato', api: apiPhato, prefix: '/v2' },
  { name: 'Laureano', api: apiLaureano, prefix: '' },
  { name: 'Laureano Soul', api: apiLaureanoSoul, prefix: '' },
];

export function PhoneDialog({ handleCloseModal }: PhoneDialogProps) {
  const [crm, setCrm] = useState('');
  const [cpf, setCpf] = useState('');
  const [phoneData, setPhoneData] = useState<PhoneData>({
    nrDddCelular: null,
    nrCelular: null,
  });

  async function deletePhone(crmValue: string) {
    const results = await Promise.allSettled(
      phoneApis.map(({ api, prefix }) =>
        api.delete(`${prefix}/medico/${crmValue}/telefone`, {
          headers: apiKeyHeaders,
          validateStatus: () => true,
        }),
      ),
    );

    if (results.every((result) => result.status === 'rejected')) {
      throw new Error('Nenhuma API respondeu à exclusão do telefone.');
    }
  }

  async function handleDeletePhone() {
    if (!crm.trim()) return alert('Por favor, insira um CRM válido.');

    try {
      await deletePhone(crm);
      handleCloseModal();
    } catch (error) {
      toast.error('Não foi possível excluir o telefone.');
    }
  }

  async function handleGetPhoneNumber(crmValue: string) {
    setCrm(crmValue);
    if (!crmValue.trim()) {
      setPhoneData({ nrDddCelular: null, nrCelular: null });
      return;
    }

    const results = await Promise.allSettled(
      phoneApis.map(({ api, prefix }) =>
        api.get(`${prefix}/medico/${crmValue}/telefone`, {
          headers: apiKeyHeaders,
          validateStatus: () => true,
        }),
      ),
    );
    const response = results.find(
      (result) =>
        result.status === 'fulfilled' &&
        result.value.status === 200 &&
        result.value.data,
    );

    if (!response || response.status !== 'fulfilled') {
      setPhoneData({ nrDddCelular: '', nrCelular: '' });
      return;
    }

    setPhoneData(response.value.data);
    const savedCpf =
      response.value.data.cpf ||
      response.value.data.nrCpf ||
      response.value.data.NR_CPF ||
      '';
    if (savedCpf) setCpf(String(savedCpf));
  }

  async function handleUpdatePhone() {
    if (!crm.trim()) return alert('Por favor, insira um CRM válido.');
    if (!phoneData.nrDddCelular || !phoneData.nrCelular) {
      return alert('Preencha o número de telefone para continuar!');
    }
    if (!cpf.replace(/\D/g, '')) {
      return alert('Informe o CPF do médico para cadastrar.');
    }

    try {
      await deletePhone(crm);
      await handleSendPhone(phoneData.nrDddCelular + phoneData.nrCelular);
      setPhoneData({ nrDddCelular: null, nrCelular: null });
      handleCloseModal();
    } catch (error) {
      toast.error('Não foi possível atualizar o telefone em nenhuma API.');
    }
  }

  async function handleSendPhone(phone: string) {
    const onlyNumbers = phone.replace(/\D/g, '');
    const ddd = onlyNumbers.substring(0, 2);
    const number = onlyNumbers.substring(2);
    if (ddd.length !== 2 || number.length < 8) {
      throw new Error('Telefone inválido.');
    }

    const normalizedCrm = crm.replace(/\D/g, '');
    const normalizedCpf = cpf.replace(/\D/g, '');
    const results = await Promise.allSettled(
      phoneApis.map(({ name, api, prefix }) =>
        api.post(
          `${prefix}/medico/telefone`,
          {
            CD_PRESTADOR: normalizedCrm,
            ...(name === 'Phato' ? { CPF: normalizedCpf } : {}),
            NR_DDD_CELULAR: ddd,
            NR_CELULAR: number,
          },
          { headers: apiKeyHeaders },
        ),
      ),
    );

    if (results.every((result) => result.status === 'rejected')) {
      throw new Error('Nenhuma API aceitou o telefone.');
    }
  }

  return (
    <S.PhoneDialogContainer>
      <header>
        <h1>Gerenciar telefone do médico</h1>
        <X size={24} weight="bold" color="black" onClick={handleCloseModal} />
      </header>
      <S.PhoneContent>
        <S.PhoneFieldContainer>
          <p>CRM do médico</p>
          <Input
            type="text"
            placeholder="CRM do médico"
            onChange={(event) => handleGetPhoneNumber(event.target.value)}
          />
        </S.PhoneFieldContainer>
        {phoneData.nrDddCelular !== null && phoneData.nrCelular !== null && (
          <S.PhoneContent>
            <S.PhoneFieldContainer>
              <p>CPF do médico</p>
              <InputMask
                type="text"
                placeholder="CPF do médico"
                value={cpf}
                onChange={(event) => setCpf(event.target.value)}
                mask="999.999.999-99"
              />
            </S.PhoneFieldContainer>
            <S.PhoneFieldContainer>
              <p>DDD</p>
              <Input
                type="text"
                placeholder="DDD do celular"
                value={phoneData.nrDddCelular}
                onChange={(event) =>
                  setPhoneData({
                    ...phoneData,
                    nrDddCelular: event.target.value,
                  })
                }
              />
            </S.PhoneFieldContainer>
            <S.PhoneFieldContainer>
              <p>Número do celular</p>
              <InputMask
                type="text"
                placeholder="Número do celular"
                value={phoneData.nrCelular}
                onChange={(event) =>
                  setPhoneData({ ...phoneData, nrCelular: event.target.value })
                }
                mask="99999-9999"
              />
            </S.PhoneFieldContainer>
          </S.PhoneContent>
        )}

        <footer>
          <Button onClick={handleDeletePhone}>Excluir</Button>
          <Button onClick={handleUpdatePhone}>Salvar</Button>
        </footer>
      </S.PhoneContent>
    </S.PhoneDialogContainer>
  );
}
